import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, CreditCard, KeyRound, FileText, Zap, Home, Check } from "lucide-react";
import { api } from "../lib/api";
import { timeAgo } from "../lib/format";
import { useLiveNotification, useRealtime } from "../contexts/RealtimeContext";

const ICON_BY_PREFIX = {
  payment: CreditCard,
  bizum: CreditCard,
  charge: CreditCard,
  bill: Zap,
  access: KeyRound,
  lock: KeyRound,
  document: FileText,
  device: Home,
  tenant: Home,
};

function pickIcon(type) {
  const suffix = String(type || "").split(".")[1] || "";
  for (const k in ICON_BY_PREFIX) if (suffix.includes(k)) return ICON_BY_PREFIX[k];
  return Bell;
}

/**
 * BellDropdown
 * @param {"sidebar"|"mobile"|"light"} variant - where it lives
 *   sidebar → desktop sidebar header (dark background, dropdown to the right)
 *   mobile  → mobile top bar (dark background, dropdown to the left)
 *   light   → any light surface (fallback)
 */
export default function BellDropdown({ variant = "light" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const ref = useRef(null);
  // El contador vive en RealtimeContext (compartido): AppLayout monta este
  // componente dos veces a la vez (sidebar escritorio + barra móvil), y
  // antes cada una pedía /notifications/unread-count por su cuenta -- dos
  // peticiones idénticas en cada carga de página. Aquí solo se lee.
  const { unreadCount: count, refreshUnreadCount: refreshCount } = useRealtime();

  const loadItems = async () => {
    try { const { data } = await api.get("/notifications?limit=10"); setItems(data); } catch {}
  };
  useEffect(() => { if (open) loadItems(); }, [open]);
  useLiveNotification(() => { if (open) loadItems(); });
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const clickItem = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); } catch {}
    setOpen(false);
    if (n.deep_link) nav(n.deep_link);
    refreshCount();
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); refreshCount(); loadItems(); } catch {}
  };

  const isDark = variant === "sidebar" || variant === "mobile";
  const buttonCls = isDark
    ? "w-9 h-9 rounded-full hover:bg-stone-100 text-stone-700"
    : "w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700";
  // Dropdown alignment: sidebar shows to the right (open outward from sidebar);
  // mobile shows aligned to the right edge but panel offsets left so it stays on-screen.
  const dropdownPositionCls =
    variant === "sidebar"
      ? "left-full ml-2 top-0"
      : "right-0 mt-2";

  return (
    <div ref={ref} className="relative" data-testid="bell-wrapper">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`${buttonCls} flex items-center justify-center relative transition-colors`}
        data-testid="bell-button"
        aria-label={t("notifications.title")}
      >
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#b35c00] text-white text-[10px] font-bold flex items-center justify-center px-1"
            data-testid="bell-badge"
          >{count > 99 ? "99+" : count}</span>
        )}
      </button>
      {open && (
        <div
          className={`absolute ${dropdownPositionCls} w-[calc(100vw-2rem)] max-w-sm sm:w-96 bg-white rounded-xl shadow-lg border border-stone-200 overflow-hidden z-50`}
          data-testid="bell-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
            <p className="font-display font-bold text-stone-900">{t("notifications.title")}</p>
            <button onClick={markAll} className="text-xs font-semibold text-[color:var(--brand-accent)]" data-testid="bell-mark-all">
              <Check className="w-3 h-3 inline mr-1" />{t("notifications.mark_all")}
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <p className="p-6 text-sm text-stone-500 text-center">{t("notifications.empty")}</p>
            ) : items.map((n) => {
              const Icon = pickIcon(n.type);
              return (
                <button key={n.id} onClick={() => clickItem(n)}
                  className={`w-full flex gap-3 items-start px-4 py-3 text-left hover:bg-stone-50 border-b border-stone-200 ${n.read ? "opacity-70" : ""}`}
                  data-testid={`bell-item-${n.id}`}>
                  <div className={`flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    n.priority === "high" ? "text-rose-700" : "text-stone-500"
                  }`}><Icon className="w-5 h-5" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-stone-900 truncate">{n.title}</p>
                    <p className="text-xs text-stone-600 line-clamp-2 mt-0.5">{n.body}</p>
                    <p className="text-xs text-stone-500 mt-1">{timeAgo(t, n.created_at)}</p>
                  </div>
                  {!n.read && <div className="w-2 h-2 rounded-full bg-[color:var(--brand-accent)] mt-2 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); nav("/notifications"); }}
            className="block w-full px-4 py-3 text-sm font-semibold text-center text-stone-700 hover:bg-stone-50 border-t border-stone-200"
            data-testid="bell-see-all">
            {t("notifications.see_all")}
          </button>
        </div>
      )}
    </div>
  );
}
